import { useState, useMemo, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Package,
  AlertTriangle,
  TrendingUp,
  Clock,
  CheckCircle2,
  ArrowRight,
  Plus,
  FileText,
  BarChart3,
  Zap,
  Brain,
  Warehouse,
  Eye,
  MapPin,
  Activity,
  Loader2,
  Monitor,
  TestTube2,
  Settings,
  RefreshCw,
  Calendar,
  ArrowDown,
  ArrowUp,
  Database,
  Trash2,
  Edit,
  ArrowRightLeft,
  Home,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { HelpButton } from "@/components/help-button";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { useNavigation } from "@/components/layout/app-layout";
import { getStockItems } from "@/services/stockDataService";
import { mockWarehouses, mockLocations } from "@/data/mockWarehousesLocations";
import { mockStockTakes } from "@/data/mockStockTakes";
import { useScreenMode, SHOW_DEMO_MODE_BADGES } from "@/providers/ModeProvider";
import { useDashboardData } from "@/hooks/use-dashboard-data";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

// Helper function to map colors to variants
const mapColorToVariant = (color: string): "success" | "warning" | "error" | "info" => {
  if (color.includes("red") || color.includes("rose") || color.includes("crimson")) {
    return "error";
  }
  if (color.includes("yellow") || color.includes("amber") || color.includes("orange")) {
    return "warning";
  }
  if (color.includes("green") || color.includes("emerald")) {
    return "success";
  }
  // All other colors (blue, purple, indigo, cyan, teal, etc.) map to info
  return "info";
};

// Types
interface QuickStat {
  label: string;
  value: string | number;
  icon: React.ElementType;
  trend?: string;
  trendUp?: boolean;
  color: string;
}

interface ActionItem {
  id: string;
  title: string;
  description: string;
  priority: "HIGH" | "MEDIUM" | "LOW";
  category: string;
  action: string;
  actionLink: string;
  filterParams?: any; // Parameters to pass to the target page
}

interface StockAlert {
  id: string;
  stockCode: string;
  description: string;
  currentStock: number;
  reorderPoint: number;
  daysUntilStockout: number;
  status: "CRITICAL" | "WARNING";
}

interface RecentActivity {
  id: string;
  type: "movement" | "order" | "receive" | "adjustment";
  description: string;
  user: string;
  timestamp: string;
  icon: React.ElementType;
}

interface DashboardSettings {
  // KPI Cards
  kpiCards: {
    totalStockValue: boolean;
    activeItems: boolean;
    lowStockAlerts: boolean;
    stockAccuracy: boolean;
    pendingOrders: boolean;
    warehouseUtilization: boolean;
    reorderPoints: boolean;
    expiringItems: boolean;
  };
  // Secondary Stats
  secondaryStats: {
    stockTakes: boolean;
    warehouses: boolean;
    categories: boolean;
  };
  // Quick Actions
  quickActions: {
    newStockTake: boolean;
    receiveStock: boolean;
    transferStock: boolean;
    adjustStock: boolean;
    checkAvailability: boolean;
    viewReports: boolean;
    manageLocations: boolean;
  };
  // Action Items
  actionItems: boolean;
  // Stock Alerts
  stockAlerts: boolean;
  // Recent Activity
  recentActivity: boolean;
  // Stock By Type
  stockByType: boolean;
  // Top Value Items
  topValueItems: boolean;
  // Stock Movement Summary
  stockMovementSummary: boolean;
}

const DEFAULT_SETTINGS: DashboardSettings = {
  kpiCards: {
    totalStockValue: true,
    activeItems: true,
    lowStockAlerts: true,
    stockAccuracy: true,
    pendingOrders: false,
    warehouseUtilization: false,
    reorderPoints: false,
    expiringItems: false,
  },
  secondaryStats: {
    stockTakes: false,
    warehouses: false,
    categories: false,
  },
  quickActions: {
    newStockTake: true,
    receiveStock: true,
    transferStock: true,
    adjustStock: true,
    checkAvailability: true,
    viewReports: true,
    manageLocations: true,
  },
  actionItems: true,
  stockAlerts: true,
  recentActivity: true,
  stockByType: true,
  topValueItems: true,
  stockMovementSummary: true,
};

export function DashboardPage() {
  const { navigateTo } = useNavigation();
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("dashboard");

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<DashboardSettings>(DEFAULT_SETTINGS);
  const [isStockMovementDialogOpen, setIsStockMovementDialogOpen] = useState(false);
  const [isCheckAvailabilityOpen, setIsCheckAvailabilityOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState<any>(null);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem("dashboardSettings");
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings));
      } catch (e) {
        console.error("Failed to parse dashboard settings:", e);
      }
    }
  }, []);

  // Save settings to localStorage
  const saveSettings = (newSettings: DashboardSettings) => {
    setSettings(newSettings);
    localStorage.setItem("dashboardSettings", JSON.stringify(newSettings));
  };

  const toggleKpiCard = (key: keyof DashboardSettings["kpiCards"]) => {
    const newSettings = {
      ...settings,
      kpiCards: { ...settings.kpiCards, [key]: !settings.kpiCards[key] },
    };
    saveSettings(newSettings);
  };

  const toggleSecondaryStat = (key: keyof DashboardSettings["secondaryStats"]) => {
    const newSettings = {
      ...settings,
      secondaryStats: { ...settings.secondaryStats, [key]: !settings.secondaryStats[key] },
    };
    saveSettings(newSettings);
  };

  const toggleQuickAction = (key: keyof DashboardSettings["quickActions"]) => {
    const newSettings = {
      ...settings,
      quickActions: { ...settings.quickActions, [key]: !settings.quickActions[key] },
    };
    saveSettings(newSettings);
  };

  const toggleSimpleSetting = (
    key: "actionItems" | "stockAlerts" | "recentActivity" | "stockByType" | "topValueItems" | "stockMovementSummary",
  ) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    saveSettings(newSettings);
  };

  const resetSettings = () => {
    saveSettings(DEFAULT_SETTINGS);
  };

  // Mode context for demo/live toggle (screen-specific)
  const { setMode, isDemo, isLive } = useScreenMode("dashboard");
  const isLiveMode = isLive;

  // Live data hook
  const { data: liveData, isLoading: isLiveLoading, fetchDashboardData } = useDashboardData();

  // Load mock data
  const mockStockItems = useMemo(() => getStockItems(), []);

  // Fetch live data when in live mode
  useEffect(() => {
    if (isLiveMode) {
      fetchDashboardData();
    }
  }, [isLiveMode, fetchDashboardData]);

  // Handle mode toggle
  const handleModeToggle = useCallback(
    (newIsLive: boolean) => {
      setMode(newIsLive ? "live" : "demo");
    },
    [setMode],
  );

  // Get current data source based on mode
  const stockItems = useMemo(() => {
    if (isLiveMode && liveData.stockItems.length > 0) {
      return liveData.stockItems.map((item) => ({
        code: item.stockCode,
        description: item.description,
        category: item.category || "Uncategorized",
        warehouseCode: item.warehouseCode || "",
        locationCode: item.locationCode || "",
        physicalQuantity: item.physicalQuantity,
        unitPrice: item.unitPrice,
        value: item.totalValue,
        inactive: item.inactive,
        lastMovementDate: item.lastMovementDate ? new Date(item.lastMovementDate) : new Date(),
        reorderPoint: item.reorderPoint,
        stockTurnRate: item.stockTurnRate || 0,
        abcClassification: (item.aBCClassification as "A" | "B" | "C") || "C",
        allocatedQuantity: item.allocatedQuantity,
        quantityDueIn: item.quantityDueIn,
        minQuantity: item.minQuantity,
        maxQuantity: item.maxQuantity,
        reorderQuantity: item.reorderQuantity,
        lastCostPrice: item.lastCostPrice,
        averageCost: item.averageCost,
        barcode: "",
      }));
    }
    return mockStockItems;
  }, [isLiveMode, liveData.stockItems, mockStockItems]);

  const warehouses = useMemo(() => {
    if (isLiveMode && liveData.warehouses.length > 0) {
      return liveData.warehouses.map((w) => ({
        code: w.warehouseCode,
        name: w.warehouseName,
        address: w.address || "",
        type: w.warehouseType as "Main" | "Satellite" | "Retail" | "Storage" | "Distribution",
        totalCapacity: w.totalCapacity || undefined,
        currentUtilization: w.currentUtilization || 0,
        active: w.active,
        defaultReceiveLocation: w.defaultReceiveLocation || undefined,
        defaultPickLocation: w.defaultPickLocation || undefined,
      }));
    }
    return mockWarehouses;
  }, [isLiveMode, liveData.warehouses]);

  const locations = useMemo(() => {
    if (isLiveMode && liveData.locations.length > 0) {
      return liveData.locations.map((l) => ({
        locationCode: l.locationCode,
        warehouseCode: l.warehouseCode,
        aisle: l.aisle || undefined,
        rack: l.rack || undefined,
        shelf: l.shelf || undefined,
        bin: l.bin || undefined,
        locationType: l.locationType as "Storage" | "Picking" | "Receiving" | "Staging" | "Shipping" | "Quarantine",
        capacity: l.capacity || undefined,
        currentStock: l.currentStock,
        utilizationPercent: l.utilizationPercent,
        pickingSequence: l.pickingSequence || undefined,
        active: l.active,
      }));
    }
    return mockLocations;
  }, [isLiveMode, liveData.locations]);

  const stockTakes = useMemo(() => {
    if (isLiveMode && liveData.stockTakes.length > 0) {
      return liveData.stockTakes.map((st) => ({
        id: String(st.stockTakeID),
        reference: st.stockTakeReference,
        description: st.description,
        type: st.stockTakeType as "Full Stock Take" | "Partial Stock Take" | "Location Audit" | "Cycle Count",
        warehouse: st.warehouse,
        warehouseCode: st.warehouseCode,
        status: st.status as
          | "Draft"
          | "Started"
          | "In Progress"
          | "Review"
          | "Complete"
          | "Completed"
          | "Scheduled"
          | "Paused"
          | "Cancelled",
        createdBy: st.createdBy,
        createdAt: new Date(st.createdAt),
        assignedTo: st.assignedTo || "Unassigned",
        startedAt: st.startedAt ? new Date(st.startedAt) : undefined,
        completedAt: st.completedAt ? new Date(st.completedAt) : undefined,
        items: [],
        totalItems: st.totalItems,
        countedItems: st.countedItems,
        varianceCount: st.varianceCount,
        notes: st.notes || undefined,
      }));
    }
    return mockStockTakes;
  }, [isLiveMode, liveData.stockTakes]);

  // Calculate statistics from current data source
  const stats = useMemo(() => {
    const activeItems = stockItems.filter((item) => !item.inactive);
    const totalValue = activeItems.reduce((sum, item) => sum + item.physicalQuantity * item.unitPrice, 0);
    const outOfStock = activeItems.filter((item) => item.physicalQuantity === 0);
    const lowStock = activeItems.filter(
      (item) => item.physicalQuantity > 0 && item.physicalQuantity <= item.reorderPoint,
    );
    const activeWarehouses = warehouses.filter((w) => w.active);
    const activeLocations = locations.filter((l) => l.active);
    const inProgressStockTakes = stockTakes.filter((st) => st.status === "In Progress" || st.status === "Started");
    const scheduledStockTakes = stockTakes.filter((st) => st.status === "Scheduled");

    const totalPhysicalStock = activeItems.reduce((sum, item) => sum + item.physicalQuantity, 0);
    const totalAllocated = activeItems.reduce((sum, item) => sum + (item.allocatedQuantity || 0), 0);
    const totalOnOrder = activeItems.reduce((sum, item) => sum + (item.quantityDueIn || 0), 0);
    const freeStock = totalPhysicalStock - totalAllocated;

    // Mock movement data (last 7 days) - in a real system this would come from the backend
    const goodsIn = 2847; // Total units received in last 7 days
    const goodsOut = 2156; // Total units dispatched in last 7 days
    const netMovement = goodsIn - goodsOut;

    return {
      totalValue,
      activeItemsCount: activeItems.length,
      outOfStockCount: outOfStock.length,
      lowStockCount: lowStock.length,
      warehouseCount: activeWarehouses.length,
      locationCount: activeLocations.length,
      inProgressStockTakesCount: inProgressStockTakes.length,
      scheduledStockTakesCount: scheduledStockTakes.length,
      outOfStockItems: outOfStock,
      lowStockItems: lowStock,
      totalPhysicalStock,
      totalAllocated,
      totalOnOrder,
      freeStock,
      goodsIn,
      goodsOut,
      netMovement,
    };
  }, [stockItems, warehouses, locations, stockTakes]);

  // Quick Stats Data (now using real data)
  const allQuickStats: Array<QuickStat & { id: keyof DashboardSettings["kpiCards"] }> = [
    {
      id: "totalStockValue",
      label: "Total Stock Value",
      value: `£${(stats.totalValue / 1000000).toFixed(2)}M`,
      icon: TrendingUp,
      trend: `${stats.activeItemsCount} active items`,
      trendUp: true,
      color: "bg-green-500",
    },
    {
      id: "activeItems",
      label: "Active Items",
      value: stats.activeItemsCount.toLocaleString(),
      icon: Package,
      trend: `${stats.warehouseCount} warehouses`,
      trendUp: true,
      color: "bg-blue-500",
    },
    {
      id: "lowStockAlerts",
      label: "Low Stock Alerts",
      value: stats.lowStockCount,
      icon: AlertTriangle,
      trend: "Below reorder point",
      trendUp: false,
      color: "bg-yellow-500",
    },
    {
      id: "stockAccuracy",
      label: "Physical Stock",
      value: stats.totalPhysicalStock.toLocaleString(),
      icon: Package,
      trend: "Total units in stock",
      trendUp: true,
      color: "bg-emerald-500",
    },
    {
      id: "pendingOrders",
      label: "Pending Orders",
      value: "24",
      icon: Clock,
      trend: "Awaiting processing",
      trendUp: false,
      color: "bg-orange-500",
    },
    {
      id: "warehouseUtilization",
      label: "Warehouse Utilisation",
      value: `${Math.round(
        warehouses.reduce((sum, w) => sum + (w.active ? w.currentUtilization : 0), 0) /
          warehouses.filter((w) => w.active).length,
      )}%`,
      icon: Warehouse,
      trend: "Average across all",
      trendUp: true,
      color: "bg-indigo-500",
    },
    {
      id: "reorderPoints",
      label: "Items at Reorder Point",
      value: stats.lowStockCount,
      icon: RefreshCw,
      trend: "Need reordering",
      trendUp: false,
      color: "bg-purple-500",
    },
    {
      id: "expiringItems",
      label: "Expiring Soon",
      value: "12",
      icon: Calendar,
      trend: "Next 30 days",
      trendUp: false,
      color: "bg-red-500",
    },
  ];

  // Filter KPI cards based on settings
  const quickStats = allQuickStats.filter((stat) => settings.kpiCards[stat.id]);

  // Action Items Data with filter parameters (using real data)
  const actionItems: ActionItem[] = useMemo(() => {
    const items: ActionItem[] = [];

    // Out of Stock Items
    if (stats.outOfStockCount > 0) {
      items.push({
        id: "1",
        title: `${stats.outOfStockCount} Items Out of Stock`,
        description: "Critical items need immediate reordering to prevent stockouts",
        priority: "HIGH",
        category: "Stock Management",
        action: "View Items",
        actionLink: "stock-items",
        filterParams: {
          filters: [
            {
              id: "filter-outofstock",
              field: "physicalQuantity",
              operator: "equals" as const,
              value: 0,
              value2: null,
            },
          ],
          logic: [],
        },
      });
    }

    // Low Stock Items
    if (stats.lowStockCount > 0) {
      items.push({
        id: "3",
        title: `${stats.lowStockCount} Items Below Reorder Point`,
        description: "Stock levels approaching minimum thresholds",
        priority: stats.lowStockCount > 20 ? "HIGH" : "MEDIUM",
        category: "Stock Management",
        action: "Review Stock",
        actionLink: "stock-items",
        filterParams: {
          filters: [
            {
              id: "filter-belowreorder",
              field: "isBelowReorderPoint",
              operator: "isYes" as const,
              value: null,
              value2: null,
            },
          ],
          logic: [],
        },
      });
    }

    // In Progress Stock Takes
    if (stats.inProgressStockTakesCount > 0) {
      items.push({
        id: "4",
        title: `${stats.inProgressStockTakesCount} Stock Takes In Progress`,
        description: "Active stock counts requiring attention",
        priority: "MEDIUM",
        category: "Stock Takes",
        action: "View Stock Takes",
        actionLink: "stock-takes",
        filterParams: {
          statusFilter: "in-progress",
        },
      });
    }

    // Scheduled Stock Takes
    if (stats.scheduledStockTakesCount > 0) {
      items.push({
        id: "5",
        title: `${stats.scheduledStockTakesCount} Scheduled Stock Takes`,
        description: "Upcoming stock counts to prepare for",
        priority: "MEDIUM",
        category: "Stock Takes",
        action: "View Schedule",
        actionLink: "stock-takes",
        filterParams: {
          statusFilter: "scheduled",
        },
      });
    }

    // Warehouse Utilisation
    const highUtilizationWarehouses = warehouses.filter((w) => w.active && w.currentUtilization > 80);
    if (highUtilizationWarehouses.length > 0) {
      items.push({
        id: "6",
        title: `${highUtilizationWarehouses.length} Warehouses Over 80% Capacity`,
        description: `${highUtilizationWarehouses.map((w) => w.name).join(", ")} need attention`,
        priority: "MEDIUM",
        category: "Warehouse Management",
        action: "View Warehouses",
        actionLink: "warehouses-locations",
      });
    }

    // ABC Classification
    const classAItems = stockItems.filter((item) => !item.inactive && item.abcClassification === "A");
    if (classAItems.length > 0) {
      items.push({
        id: "7",
        title: `${classAItems.length} High-Value Items (Class A)`,
        description: "Monitor your most valuable inventory items",
        priority: "LOW",
        category: "Analytics",
        action: "View ABC Analysis",
        actionLink: "abc-classification",
      });
    }

    // AI Forecasting
    items.push({
      id: "8",
      title: "AI Demand Forecasting Available",
      description: "Optimise inventory with predictive analytics",
      priority: "LOW",
      category: "Analytics",
      action: "View Forecast",
      actionLink: "ai-forecasting",
    });

    return items;
  }, [stats, warehouses, stockItems]);

  // Stock Alerts Data (using real data)
  const stockAlerts: StockAlert[] = useMemo(() => {
    const alerts: StockAlert[] = [];

    // Combine out of stock and low stock items
    const criticalItems = [...stats.outOfStockItems, ...stats.lowStockItems]
      .sort((a, b) => {
        // Sort by severity: out of stock first, then by how far below reorder point
        if (a.physicalQuantity === 0 && b.physicalQuantity > 0) return -1;
        if (a.physicalQuantity > 0 && b.physicalQuantity === 0) return 1;
        return a.physicalQuantity - b.physicalQuantity;
      })
      .slice(0, 10); // Show top 10 alerts

    criticalItems.forEach((item, index) => {
      // Estimate days until stockout based on stock turn rate
      let daysUntilStockout = 0;
      if (item.physicalQuantity > 0 && item.stockTurnRate) {
        // If stock turn rate is 12 (monthly), then daily usage is totalValue/365
        const estimatedDailyUsage = (item.stockTurnRate / 365) * item.physicalQuantity;
        daysUntilStockout = estimatedDailyUsage > 0 ? Math.floor(item.physicalQuantity / estimatedDailyUsage) : 30;
      }

      alerts.push({
        id: String(index + 1),
        stockCode: item.code,
        description: item.description,
        currentStock: item.physicalQuantity,
        reorderPoint: item.reorderPoint,
        daysUntilStockout,
        status:
          item.physicalQuantity === 0
            ? "CRITICAL"
            : item.physicalQuantity <= item.reorderPoint * 0.5
            ? "CRITICAL"
            : "WARNING",
      });
    });

    return alerts;
  }, [stats]);

  // Recent Activity Data (using real data from stock takes and items)
  const recentActivity: RecentActivity[] = useMemo(() => {
    const activities: RecentActivity[] = [];

    // Add recent stock take activities
    const recentStockTakes = [...stockTakes]
      .sort((a, b) => {
        const dateA = a.startedAt || a.createdAt;
        const dateB = b.startedAt || b.createdAt;
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 3);

    recentStockTakes.forEach((st, index) => {
      const date = st.startedAt || st.createdAt;
      const hoursAgo = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60));
      const timeStr =
        hoursAgo < 1
          ? "Less than 1 hour ago"
          : hoursAgo === 1
          ? "1 hour ago"
          : hoursAgo < 24
          ? `${hoursAgo} hours ago`
          : `${Math.floor(hoursAgo / 24)} days ago`;

      activities.push({
        id: `st-${index}`,
        type: "adjustment",
        description: `Stock Take ${st.reference}: ${st.status} - ${st.warehouse} (${st.countedItems}/${st.totalItems} counted)`,
        user: st.assignedTo,
        timestamp: timeStr,
        icon: FileText,
      });
    });

    // Add recent stock movements based on item last movement dates
    const recentlyMovedItems = [...stockItems]
      .filter((item) => item.lastMovementDate)
      .sort((a, b) => {
        const dateA = a.lastMovementDate?.getTime() || 0;
        const dateB = b.lastMovementDate?.getTime() || 0;
        return dateB - dateA;
      })
      .slice(0, 5);

    recentlyMovedItems.forEach((item, index) => {
      if (!item.lastMovementDate) return;

      const hoursAgo = Math.floor((Date.now() - item.lastMovementDate.getTime()) / (1000 * 60 * 60));
      const timeStr =
        hoursAgo < 1
          ? "Less than 1 hour ago"
          : hoursAgo === 1
          ? "1 hour ago"
          : hoursAgo < 24
          ? `${hoursAgo} hours ago`
          : `${Math.floor(hoursAgo / 24)} days ago`;

      const warehouse = warehouses.find((w) => w.code === item.warehouseCode);

      activities.push({
        id: `mov-${index}`,
        type: "movement",
        description: `Stock movement: ${item.code} - ${item.description} (${item.physicalQuantity} units in ${
          warehouse?.name || item.warehouseCode
        })`,
        user: "System",
        timestamp: timeStr,
        icon: ArrowRight,
      });
    });

    // Sort all activities by most recent
    return activities
      .sort((a, b) => {
        // Simple sort by extracting numbers from timestamp strings
        const getMinutes = (ts: string) => {
          if (ts.includes("Less than")) return 0;
          if (ts.includes("hour ago") && !ts.includes("hours")) return 60;
          if (ts.includes("hours ago")) return parseInt(ts) * 60;
          if (ts.includes("days ago")) return parseInt(ts) * 24 * 60;
          return 0;
        };
        return getMinutes(a.timestamp) - getMinutes(b.timestamp);
      })
      .slice(0, 8); // Show top 8 activities
  }, [stockTakes, stockItems, warehouses]);

  // Get priority badge color
  const getPriorityColor = (priority: ActionItem["priority"]) => {
    switch (priority) {
      case "HIGH":
        return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
      case "MEDIUM":
        return "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300";
      case "LOW":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
    }
  };

  // Get alert status color
  const getAlertColor = (status: StockAlert["status"]) => {
    return status === "CRITICAL"
      ? "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
      : "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-3">
              <Home className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold tracking-tight">Home</h1>
            </div>
            {SHOW_DEMO_MODE_BADGES && (
              <>
                {isLiveMode ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    Live Data
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                    Demo Mode
                  </Badge>
                )}
                {isLiveLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <div className="flex items-center gap-2">
                  <TestTube2 className={cn("h-4 w-4", isDemo ? "text-yellow-600" : "text-muted-foreground")} />
                  <Switch
                    id="dashboard-mode"
                    checked={isLiveMode}
                    onCheckedChange={handleModeToggle}
                    aria-label="Toggle between demo and live mode"
                  />
                  <Monitor className={cn("h-4 w-4", isLiveMode ? "text-green-600" : "text-muted-foreground")} />
                  <Label htmlFor="dashboard-mode" className="text-sm text-muted-foreground cursor-pointer">
                    {isLiveMode ? "Live" : "Demo"}
                  </Label>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="default" onClick={() => setIsSettingsOpen(true)} className="gap-2">
              <Settings className="h-4 w-4" />
              Edit Dashboard
            </Button>
            <HelpButton onClick={toggleHelp} variant="outline" size="default" showLabel />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {(() => {
        const allQuickActions = [
          { id: "newStockTake" as const, icon: FileText, label: "New Stock Take", route: "stock-takes-home" },
          { id: "receiveStock" as const, icon: Package, label: "Receive Stock", route: "receive-goods" },
          { id: "transferStock" as const, icon: ArrowRight, label: "Transfer Stock", route: "stock-transfers/create/step1" },
          { id: "adjustStock" as const, icon: Plus, label: "New Stock Movement", route: null, isModal: true, modalType: "stockMovement" },
          { id: "checkAvailability" as const, icon: Search, label: "Check Availability", route: null, isModal: true, modalType: "checkAvailability" },
          { id: "manageLocations" as const, icon: MapPin, label: "Manage Locations", route: "warehouses-locations" },
        ];
        const visibleActions = allQuickActions.filter((action) => settings.quickActions[action.id]);

        if (visibleActions.length === 0) return null;

        return (
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {visibleActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <Button
                      key={action.id}
                      variant="outline"
                      className="h-auto gap-2 px-5 py-3"
                      onClick={() => {
                        if (action.isModal) {
                          if (action.modalType === "stockMovement") {
                            setIsStockMovementDialogOpen(true);
                          } else if (action.modalType === "checkAvailability") {
                            setIsCheckAvailabilityOpen(true);
                            setSearchQuery("");
                            setSelectedItem(null);
                          }
                        } else {
                          navigateTo(action.route);
                        }
                      }}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-sm font-medium whitespace-nowrap">{action.label}</span>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Quick Stats */}
      {quickStats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickStats.map((stat) => (
            <StaticMetricCardAlt
              key={stat.id}
              title={stat.label}
              value={typeof stat.value === "number" ? stat.value.toLocaleString() : stat.value.toString()}
              change={stat.trendUp ? "↑" : "↓"}
              changeLabel={stat.trend || ""}
              icon={stat.icon as any}
              variant={mapColorToVariant(stat.color)}
            />
          ))}
        </div>
      )}

      {/* Secondary Stats - Stock Takes & Locations */}
      {(settings.secondaryStats.stockTakes ||
        settings.secondaryStats.warehouses ||
        settings.secondaryStats.categories) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {settings.secondaryStats.stockTakes && (
            <StaticMetricCardAlt
              title="Active Stock Takes"
              value={stats.inProgressStockTakesCount.toString()}
              change="→"
              changeLabel={`${stats.scheduledStockTakesCount} scheduled`}
              icon={Activity}
              variant="info"
            />
          )}

          {settings.secondaryStats.warehouses && (
            <StaticMetricCardAlt
              title="Warehouses"
              value={stats.warehouseCount.toString()}
              change="→"
              changeLabel={`${stats.locationCount} locations`}
              icon={Warehouse}
              variant="info"
            />
          )}

          {settings.secondaryStats.categories && (
            <StaticMetricCardAlt
              title="Categories"
              value={new Set(stockItems.filter((i) => !i.inactive).map((i) => i.category)).size.toString()}
              change="→"
              changeLabel="Product categories"
              icon={Package}
              variant="info"
            />
          )}
        </div>
      )}

      {/* Main Content Widgets */}
      {(settings.actionItems || settings.stockAlerts || settings.recentActivity) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Action Items Widget */}
          {settings.actionItems && (
            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Action Items
                  <Badge variant="secondary" className="ml-auto">
                    {actionItems.filter((item) => item.priority === "HIGH").length}
                  </Badge>
                </CardTitle>
                <CardDescription>Tasks requiring your attention</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  {actionItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col gap-2 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-sm">{item.title}</h4>
                            <Badge className={getPriorityColor(item.priority)} variant="secondary">
                              {item.priority}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{item.description}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => navigateTo(item.actionLink, item.filterParams)}
                      >
                        {item.action}
                        <ArrowRight className="h-3 w-3 ml-2" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stock Alerts Widget */}
          {settings.stockAlerts && (
            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Stock Alerts
                  <Badge variant="secondary" className="ml-auto">
                    {stockAlerts.filter((alert) => alert.status === "CRITICAL").length}
                  </Badge>
                </CardTitle>
                <CardDescription>Items requiring immediate attention</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  {stockAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="flex flex-col gap-2 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold font-mono text-sm">{alert.stockCode}</h4>
                            <Badge className={getAlertColor(alert.status)} variant="secondary">
                              {alert.status}
                            </Badge>
                          </div>
                          <p className="text-xs">{alert.description}</p>
                          <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                            <span>Current: {alert.currentStock} units</span>
                            <span>Reorder: {alert.reorderPoint} units</span>
                            <span className="font-semibold text-red-600">
                              {alert.daysUntilStockout === 0
                                ? "OUT OF STOCK"
                                : `${alert.daysUntilStockout} days until stockout`}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            navigateTo("stock-items", {
                              filters: [
                                {
                                  id: `filter-${Date.now()}`,
                                  field: "stockCode",
                                  operator: "equals",
                                  value: alert.stockCode,
                                  value2: null,
                                },
                              ],
                              logic: [],
                            });
                          }}
                        >
                          View Item
                          <Eye className="h-3 w-3 ml-2" />
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          className="flex-1 relative"
                          disabled
                          title="Purchase Order creation will be available soon via MFE integration"
                        >
                          Create PO
                          <Plus className="h-3 w-3 ml-2" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Activity Widget */}
          {settings.recentActivity && (
            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Recent Activity
                </CardTitle>
                <CardDescription>Latest operations and changes</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  {recentActivity.map((activity) => {
                    const Icon = activity.icon;
                    return (
                      <div
                        key={activity.id}
                        className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="p-2 rounded-lg bg-muted">
                          <Icon className="h-3 w-3" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-xs font-medium">{activity.description}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{activity.user}</span>
                            <span>•</span>
                            <span>{activity.timestamp}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Stock By Type, Top Value Items & Stock Movement Summary */}
      {(settings.stockByType || settings.topValueItems || settings.stockMovementSummary) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {settings.stockByType && (
            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Stock By Type
                </CardTitle>
                <CardDescription>Inventory breakdown by category</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  {(() => {
                    // Calculate stock by category
                    const categoryStats = mockStockItems
                      .filter((item) => !item.inactive)
                      .reduce((acc, item) => {
                        const category = item.category || "Uncategorized";
                        if (!acc[category]) {
                          acc[category] = { count: 0, value: 0 };
                        }
                        acc[category].count += 1;
                        acc[category].value += item.physicalQuantity * item.unitPrice;
                        return acc;
                      }, {} as Record<string, { count: number; value: number }>);

                    // Sort by value and get top 5
                    const sortedCategories = Object.entries(categoryStats)
                      .sort(([, a], [, b]) => b.value - a.value)
                      .slice(0, 5);

                    return sortedCategories.map(([category, data]) => (
                      <div key={category} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">{category}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{data.count} items</p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold">
                            £{(data.value / 1000000).toFixed(2)}M
                          </div>
                          <div className="text-xs text-muted-foreground">Total Value</div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {settings.topValueItems && (
            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Top Value Items
                </CardTitle>
                <CardDescription>Highest value inventory items</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  {mockStockItems
                    .filter((item) => !item.inactive)
                    .map((item) => ({
                      ...item,
                      totalValue: item.physicalQuantity * item.unitPrice,
                    }))
                    .sort((a, b) => b.totalValue - a.totalValue)
                    .slice(0, 5)
                    .map((item) => (
                      <div key={item.code} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">{item.description}</h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            {item.code} • {item.physicalQuantity} units
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold">
                            £{(item.totalValue / 1000).toFixed(1)}K
                          </div>
                          <div className="text-xs text-muted-foreground">
                            £{item.unitPrice.toFixed(2)}/unit
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {settings.stockMovementSummary && (
            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Stock Movement Summary
                </CardTitle>
                <CardDescription>Stock allocation and recent activity</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
                  {/* Stock Allocation Overview */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Stock Allocation Overview
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-foreground">Physical Stock</span>
                        <span className="text-sm font-bold text-foreground">
                          {stats.totalPhysicalStock.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Database className="h-3.5 w-3.5 text-red-500" />
                          <span className="text-sm text-red-600 dark:text-red-400">Allocated</span>
                        </div>
                        <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                          {stats.totalAllocated.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <ArrowDown className="h-3.5 w-3.5 text-green-500" />
                          <span className="text-sm text-green-600 dark:text-green-400">On Order</span>
                        </div>
                        <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {stats.totalOnOrder.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-base font-bold text-teal-700 dark:text-teal-400">Free Stock</span>
                        <span className="text-lg font-bold text-teal-700 dark:text-teal-400">
                          {stats.freeStock.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Stacked Bar Chart */}
                    <div className="space-y-1.5">
                      <div className="h-6 w-full flex rounded-md overflow-hidden">
                        {stats.freeStock > 0 && (
                          <div
                            className="bg-teal-500 dark:bg-teal-600"
                            style={{
                              width: `${(stats.freeStock / stats.totalPhysicalStock) * 100}%`,
                            }}
                            title={`Free Stock: ${stats.freeStock.toLocaleString()}`}
                          />
                        )}
                        {stats.totalAllocated > 0 && (
                          <div
                            className="bg-red-500 dark:bg-red-600"
                            style={{
                              width: `${(stats.totalAllocated / stats.totalPhysicalStock) * 100}%`,
                            }}
                            title={`Allocated: ${stats.totalAllocated.toLocaleString()}`}
                          />
                        )}
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {((stats.freeStock / stats.totalPhysicalStock) * 100).toFixed(0)}% Free
                        </span>
                        <span>
                          {((stats.totalAllocated / stats.totalPhysicalStock) * 100).toFixed(0)}% Allocated
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Movement Trends */}
                  <div className="space-y-3 pt-3 border-t">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Movement Trends (Last 7 Days)
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2.5 border rounded-lg bg-green-50 dark:bg-green-950/20">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded bg-green-100 dark:bg-green-900/30">
                            <ArrowUp className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                          </div>
                          <span className="text-sm font-medium">Goods In</span>
                        </div>
                        <span className="text-sm font-bold text-green-700 dark:text-green-400">
                          {stats.goodsIn.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-2.5 border rounded-lg bg-red-50 dark:bg-red-950/20">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded bg-red-100 dark:bg-red-900/30">
                            <ArrowDown className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                          </div>
                          <span className="text-sm font-medium">Goods Out</span>
                        </div>
                        <span className="text-sm font-bold text-red-700 dark:text-red-400">
                          {stats.goodsOut.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-2.5 border rounded-lg bg-muted/50">
                        <span className="text-sm font-semibold">Net Movement</span>
                        <span
                          className={cn(
                            "text-sm font-bold",
                            stats.netMovement > 0
                              ? "text-green-700 dark:text-green-400"
                              : stats.netMovement < 0
                                ? "text-red-700 dark:text-red-400"
                                : "text-muted-foreground",
                          )}
                        >
                          {stats.netMovement > 0 ? "+" : ""}
                          {stats.netMovement.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Mini bar chart for daily movement */}
                    <div className="pt-2">
                      <div className="flex items-end justify-between gap-1 h-16">
                        {[
                          { in: 420, out: 310 },
                          { in: 385, out: 295 },
                          { in: 410, out: 320 },
                          { in: 395, out: 305 },
                          { in: 425, out: 315 },
                          { in: 405, out: 308 },
                          { in: 407, out: 303 },
                        ].map((day, idx) => {
                          const maxValue = 450;
                          const inHeight = (day.in / maxValue) * 100;
                          const outHeight = (day.out / maxValue) * 100;
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center gap-0.5">
                              <div
                                className="w-full bg-green-500 dark:bg-green-600 rounded-t"
                                style={{ height: `${inHeight}%` }}
                                title={`In: ${day.in}`}
                              />
                              <div
                                className="w-full bg-red-500 dark:bg-red-600 rounded-b"
                                style={{ height: `${outHeight}%` }}
                                title={`Out: ${day.out}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground mt-1.5">
                        <span>7 days ago</span>
                        <span>Today</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Dashboard Settings Dialog */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Customise Dashboard
            </DialogTitle>
            <DialogDescription>
              Show or hide sections to personalize your dashboard view. Your preferences will be saved automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* KPI Cards Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-1">KPI Cards</h3>
                <p className="text-xs text-muted-foreground">Select which metrics to display</p>
              </div>
              <div className="grid grid-cols-2 gap-3 pl-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="totalStockValue"
                    checked={settings.kpiCards.totalStockValue}
                    onCheckedChange={() => toggleKpiCard("totalStockValue")}
                  />
                  <Label htmlFor="totalStockValue" className="text-sm cursor-pointer">
                    Total Stock Value
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="activeItems"
                    checked={settings.kpiCards.activeItems}
                    onCheckedChange={() => toggleKpiCard("activeItems")}
                  />
                  <Label htmlFor="activeItems" className="text-sm cursor-pointer">
                    Active Items
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="lowStockAlerts"
                    checked={settings.kpiCards.lowStockAlerts}
                    onCheckedChange={() => toggleKpiCard("lowStockAlerts")}
                  />
                  <Label htmlFor="lowStockAlerts" className="text-sm cursor-pointer">
                    Low Stock Alerts
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="stockAccuracy"
                    checked={settings.kpiCards.stockAccuracy}
                    onCheckedChange={() => toggleKpiCard("stockAccuracy")}
                  />
                  <Label htmlFor="stockAccuracy" className="text-sm cursor-pointer">
                    Stock Accuracy
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="pendingOrders"
                    checked={settings.kpiCards.pendingOrders}
                    onCheckedChange={() => toggleKpiCard("pendingOrders")}
                  />
                  <Label htmlFor="pendingOrders" className="text-sm cursor-pointer">
                    Pending Orders
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="warehouseUtilization"
                    checked={settings.kpiCards.warehouseUtilization}
                    onCheckedChange={() => toggleKpiCard("warehouseUtilization")}
                  />
                  <Label htmlFor="warehouseUtilization" className="text-sm cursor-pointer">
                    Warehouse Utilisation
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="reorderPoints"
                    checked={settings.kpiCards.reorderPoints}
                    onCheckedChange={() => toggleKpiCard("reorderPoints")}
                  />
                  <Label htmlFor="reorderPoints" className="text-sm cursor-pointer">
                    Reorder Points
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="expiringItems"
                    checked={settings.kpiCards.expiringItems}
                    onCheckedChange={() => toggleKpiCard("expiringItems")}
                  />
                  <Label htmlFor="expiringItems" className="text-sm cursor-pointer">
                    Expiring Items
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Secondary Stats Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-1">Secondary Statistics</h3>
                <p className="text-xs text-muted-foreground">Additional metrics</p>
              </div>
              <div className="grid grid-cols-2 gap-3 pl-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="stockTakes"
                    checked={settings.secondaryStats.stockTakes}
                    onCheckedChange={() => toggleSecondaryStat("stockTakes")}
                  />
                  <Label htmlFor="stockTakes" className="text-sm cursor-pointer">
                    Active Stock Takes
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="warehouses"
                    checked={settings.secondaryStats.warehouses}
                    onCheckedChange={() => toggleSecondaryStat("warehouses")}
                  />
                  <Label htmlFor="warehouses" className="text-sm cursor-pointer">
                    Warehouses
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="categories"
                    checked={settings.secondaryStats.categories}
                    onCheckedChange={() => toggleSecondaryStat("categories")}
                  />
                  <Label htmlFor="categories" className="text-sm cursor-pointer">
                    Categories
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Quick Actions Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-1">Quick Actions</h3>
                <p className="text-xs text-muted-foreground">Select which shortcuts to display</p>
              </div>
              <div className="grid grid-cols-2 gap-3 pl-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="newStockTake"
                    checked={settings.quickActions.newStockTake}
                    onCheckedChange={() => toggleQuickAction("newStockTake")}
                  />
                  <Label htmlFor="newStockTake" className="text-sm cursor-pointer">
                    New Stock Take
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="receiveStock"
                    checked={settings.quickActions.receiveStock}
                    onCheckedChange={() => toggleQuickAction("receiveStock")}
                  />
                  <Label htmlFor="receiveStock" className="text-sm cursor-pointer">
                    Receive Stock
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="transferStock"
                    checked={settings.quickActions.transferStock}
                    onCheckedChange={() => toggleQuickAction("transferStock")}
                  />
                  <Label htmlFor="transferStock" className="text-sm cursor-pointer">
                    Transfer Stock
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="adjustStock"
                    checked={settings.quickActions.adjustStock}
                    onCheckedChange={() => toggleQuickAction("adjustStock")}
                  />
                  <Label htmlFor="adjustStock" className="text-sm cursor-pointer">
                    Adjust Stock
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="checkAvailability"
                    checked={settings.quickActions.checkAvailability}
                    onCheckedChange={() => toggleQuickAction("checkAvailability")}
                  />
                  <Label htmlFor="checkAvailability" className="text-sm cursor-pointer">
                    Check Availability
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="viewReports"
                    checked={settings.quickActions.viewReports}
                    onCheckedChange={() => toggleQuickAction("viewReports")}
                  />
                  <Label htmlFor="viewReports" className="text-sm cursor-pointer">
                    View Reports
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="manageLocations"
                    checked={settings.quickActions.manageLocations}
                    onCheckedChange={() => toggleQuickAction("manageLocations")}
                  />
                  <Label htmlFor="manageLocations" className="text-sm cursor-pointer">
                    Manage Locations
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Other Sections */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-1">Other Sections</h3>
                <p className="text-xs text-muted-foreground">Additional dashboard components</p>
              </div>
              <div className="grid grid-cols-2 gap-3 pl-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="actionItems"
                    checked={settings.actionItems}
                    onCheckedChange={() => toggleSimpleSetting("actionItems")}
                  />
                  <Label htmlFor="actionItems" className="text-sm cursor-pointer">
                    Action Items
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="stockAlerts"
                    checked={settings.stockAlerts}
                    onCheckedChange={() => toggleSimpleSetting("stockAlerts")}
                  />
                  <Label htmlFor="stockAlerts" className="text-sm cursor-pointer">
                    Stock Alerts
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="recentActivity"
                    checked={settings.recentActivity}
                    onCheckedChange={() => toggleSimpleSetting("recentActivity")}
                  />
                  <Label htmlFor="recentActivity" className="text-sm cursor-pointer">
                    Recent Activity
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="stockByType"
                    checked={settings.stockByType}
                    onCheckedChange={() => toggleSimpleSetting("stockByType")}
                  />
                  <Label htmlFor="stockByType" className="text-sm cursor-pointer">
                    Stock By Type
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="topValueItems"
                    checked={settings.topValueItems}
                    onCheckedChange={() => toggleSimpleSetting("topValueItems")}
                  />
                  <Label htmlFor="topValueItems" className="text-sm cursor-pointer">
                    Top Value Items
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="stockMovementSummary"
                    checked={settings.stockMovementSummary}
                    onCheckedChange={() => toggleSimpleSetting("stockMovementSummary")}
                  />
                  <Label htmlFor="stockMovementSummary" className="text-sm cursor-pointer">
                    Stock Movement Summary
                  </Label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex items-center justify-between">
            <Button variant="outline" onClick={resetSettings} className="mr-auto">
              Reset to Default
            </Button>
            <Button onClick={() => setIsSettingsOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Movement Dialog */}
      <Dialog open={isStockMovementDialogOpen} onOpenChange={setIsStockMovementDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Stock Movement</DialogTitle>
            <DialogDescription>
              Select the type of stock movement you want to create
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 py-4">
            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-blue-50 hover:border-blue-300"
              onClick={() => {
                setIsStockMovementDialogOpen(false);
                navigateTo("add-movement", { type: "Sales" });
              }}
            >
              <div className="rounded-full bg-blue-100 p-2">
                <ArrowDown className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Sales</div>
                <div className="text-sm text-muted-foreground">Record stock sold to customers</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-green-50 hover:border-green-300"
              onClick={() => {
                setIsStockMovementDialogOpen(false);
                navigateTo("add-movement", { type: "Purchase" });
              }}
            >
              <div className="rounded-full bg-green-100 p-2">
                <ArrowUp className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Purchase</div>
                <div className="text-sm text-muted-foreground">Record stock received from suppliers</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-purple-50 hover:border-purple-300"
              onClick={() => {
                setIsStockMovementDialogOpen(false);
                navigateTo("add-movement", { type: "Opening Balance" });
              }}
            >
              <div className="rounded-full bg-purple-100 p-2">
                <Database className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Opening Balance</div>
                <div className="text-sm text-muted-foreground">Set initial stock quantities</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-red-50 hover:border-red-300"
              onClick={() => {
                setIsStockMovementDialogOpen(false);
                navigateTo("add-movement", { type: "Write-off" });
              }}
            >
              <div className="rounded-full bg-red-100 p-2">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Write-off</div>
                <div className="text-sm text-muted-foreground">Remove damaged or obsolete stock</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-orange-50 hover:border-orange-300"
              onClick={() => {
                setIsStockMovementDialogOpen(false);
                navigateTo("stock-transfers/create/step1");
              }}
            >
              <div className="rounded-full bg-orange-100 p-2">
                <ArrowRightLeft className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Stock Transfer</div>
                <div className="text-sm text-muted-foreground">Move stock between locations</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-gray-50 hover:border-gray-300"
              onClick={() => {
                setIsStockMovementDialogOpen(false);
                navigateTo("add-movement", { type: "Manual Adjustment" });
              }}
            >
              <div className="rounded-full bg-gray-100 p-2">
                <Edit className="h-5 w-5 text-gray-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Manual Adjustment</div>
                <div className="text-sm text-muted-foreground">Correct stock quantities</div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Check Availability Dialog */}
      <Dialog open={isCheckAvailabilityOpen} onOpenChange={setIsCheckAvailabilityOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-primary" />
              Check Stock Availability
            </DialogTitle>
            <DialogDescription>
              Search for an item to view its current stock levels and availability
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Search Input */}
            <div className="space-y-2">
              <Label htmlFor="item-search">Search Item</Label>
              <Input
                id="item-search"
                placeholder="Search by SKU, name, or barcode..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedItem(null);
                }}
                autoFocus
              />
            </div>

            {/* Search Results */}
            {searchQuery.length > 0 && !selectedItem && (() => {
              const filteredItems = stockItems.filter(item => 
                !item.inactive && (
                  item.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.barcode?.toLowerCase().includes(searchQuery.toLowerCase())
                )
              ).slice(0, 10);

              if (filteredItems.length === 0) {
                return (
                  <Card className="p-4">
                    <p className="text-sm text-muted-foreground text-center">No items found matching "{searchQuery}"</p>
                  </Card>
                );
              }

              return (
                <div className="space-y-2">
                  <Label>Search Results ({filteredItems.length})</Label>
                  <div className="border rounded-md divide-y max-h-[200px] overflow-y-auto">
                    {filteredItems.map((item) => (
                      <button
                        key={item.id}
                        className="w-full p-3 text-left hover:bg-muted transition-colors flex items-center justify-between"
                        onClick={() => {
                          setSelectedItem(item);
                          setSearchQuery(item.name);
                        }}
                      >
                        <div className="flex-1">
                          <div className="font-medium">{item.name}</div>
                          <div className="text-xs text-muted-foreground">SKU: {item.sku}</div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Selected Item Details */}
            {selectedItem && (() => {
              const freeQuantity = selectedItem.physicalQuantity - selectedItem.allocatedQuantity;
              const getFreeQuantityColor = () => {
                const buffer = selectedItem.minQuantity * 0.2;
                if (freeQuantity > selectedItem.minQuantity) {
                  return "text-green-600 dark:text-green-400";
                } else if (freeQuantity >= selectedItem.minQuantity - buffer) {
                  return "text-orange-600 dark:text-orange-400";
                } else {
                  return "text-red-600 dark:text-red-400";
                }
              };

              const getMarkerPosition = (value: number) => {
                if (selectedItem.maxQuantity === 0) return 0;
                return (value / selectedItem.maxQuantity) * 100;
              };

              return (
                <div className="space-y-4">
                  <Separator />
                  
                  {/* Item Header */}
                  <div>
                    <h3 className="text-lg font-semibold">{selectedItem.name}</h3>
                    <p className="text-sm text-muted-foreground">SKU: {selectedItem.sku}</p>
                  </div>

                  {/* Current Stock Levels */}
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Current Stock Levels</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Physical Quantity Card */}
                      <Card className="bg-muted/30">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-xs font-medium text-muted-foreground">
                            Physical Quantity
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold text-muted-foreground">{selectedItem.physicalQuantity}</div>
                        </CardContent>
                      </Card>

                      {/* Allocated Quantity Card */}
                      <Card className="bg-muted/30">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-xs font-medium text-muted-foreground">
                            Allocated Quantity
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold text-muted-foreground">
                            {selectedItem.allocatedQuantity}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Quantity Due In Card */}
                      <Card className="bg-muted/30">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-xs font-medium text-muted-foreground">
                            Quantity Due In
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold text-muted-foreground">
                            {selectedItem.quantityDueIn}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Free Quantity Card */}
                      <Card className="border-2">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-xs font-medium text-muted-foreground">
                            Free Quantity
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className={`text-2xl font-bold ${getFreeQuantityColor()}`}>
                            {freeQuantity}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Stock Level Indicator */}
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Stock Level Indicator</h4>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="space-y-4">
                          {/* Progress Bar */}
                          <div className="relative h-12 rounded-md overflow-hidden border">
                            {/* Red Zone */}
                            <div
                              className="absolute h-full bg-red-200 dark:bg-red-900/30"
                              style={{
                                left: 0,
                                width: `${getMarkerPosition(selectedItem.minQuantity)}%`,
                              }}
                            />
                            {/* Orange Zone */}
                            <div
                              className="absolute h-full bg-orange-200 dark:bg-orange-900/30"
                              style={{
                                left: `${getMarkerPosition(selectedItem.minQuantity)}%`,
                                width: `${getMarkerPosition(selectedItem.reorderPoint) - getMarkerPosition(selectedItem.minQuantity)}%`,
                              }}
                            />
                            {/* Green Zone */}
                            <div
                              className="absolute h-full bg-green-200 dark:bg-green-900/30"
                              style={{
                                left: `${getMarkerPosition(selectedItem.reorderPoint)}%`,
                                width: `${100 - getMarkerPosition(selectedItem.reorderPoint)}%`,
                              }}
                            />

                            {/* Current Stock Marker */}
                            <div
                              className="absolute top-0 bottom-0 w-1 bg-primary z-10"
                              style={{
                                left: `${getMarkerPosition(selectedItem.physicalQuantity)}%`,
                              }}
                            >
                              <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-semibold text-primary">
                                Current: {selectedItem.physicalQuantity}
                              </div>
                            </div>
                          </div>

                          {/* Markers Legend */}
                          <div className="flex justify-between text-xs text-muted-foreground pt-2">
                            <div className="flex flex-col items-start">
                              <span className="font-medium">Min</span>
                              <span>{selectedItem.minQuantity}</span>
                            </div>
                            <div className="flex flex-col items-center">
                              <span className="font-medium">Reorder</span>
                              <span>{selectedItem.reorderPoint}</span>
                            </div>
                            <div className="flex flex-col items-end">
                              <span className="font-medium">Max</span>
                              <span>{selectedItem.maxQuantity}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCheckAvailabilityOpen(false)}>
              Close
            </Button>
            {selectedItem && (
              <Button onClick={() => {
                setIsCheckAvailabilityOpen(false);
                navigateTo("stock-items");
              }}>
                View All Stock Items
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contextual Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
        onNavigateToPage={(pageId) => navigateTo(pageId)}
      />
    </div>
  );
}
